// classmom_client/src/utils/auth.js - Frontend Authentication Utilities
import axios from 'axios'
import { useToast } from 'vue-toastification'

// Auth state
let authState = false
let currentUser = null

/**
 * Check if user is authenticated
 * @returns {boolean} Authentication status
 */
export const getIsAuthenticated = () => authState

/**
 * Get current user data
 * @returns {Object|null} User data or null
 */
export const getCurrentUser = () => currentUser

/**
 * Get user role
 * @returns {string|null} User role or null
 */
export const getUserRole = () => currentUser?.role || null

/**
 * Check authentication status from localStorage
 */
export const checkAuth = () => {
  const token = localStorage.getItem('token')
  const userData = localStorage.getItem('user')
  
  if (token && userData) {
    try {
      currentUser = JSON.parse(userData)
      authState = true
      
      // Set up axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      
      return true
    } catch (error) {
      console.error('Error parsing stored user data:', error)
      clearAuth()
      return false
    }
  } else {
    clearAuth()
    return false
  }
}

/**
 * Store authentication data
 * @param {string} token - JWT token
 * @param {Object} user - User data
 */
export const setAuth = (token, user) => {
  localStorage.setItem('token', token)
  localStorage.setItem('user', JSON.stringify(user))
  
  currentUser = user
  authState = true
  
  // Set up axios default header
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
}

/**
 * Clear authentication data
 */
export const clearAuth = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  
  currentUser = null
  authState = false
  
  // Remove axios default header
  delete axios.defaults.headers.common['Authorization']
  
  console.log('🔓 Authentication cleared')
}

/**
 * Login user
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {boolean} rememberMe - Remember login
 * @returns {Promise<Object>} Login result
 */
export const login = async (email, password, rememberMe = false) => {
  try {
    const response = await axios.post('/api/auth/login', {
      email: email.trim().toLowerCase(),
      password,
      rememberMe
    })
    
    const { token, user } = response.data
    
    // Store auth data
    setAuth(token, user)
    
    return {
      success: true,
      user,
      token
    }
  } catch (error) {
    console.error('Login error:', error)
    
    if (error.response) {
      return {
        success: false,
        error: error.response.data.error || 'Login failed',
        code: error.response.data.code
      }
    } else {
      return {
        success: false,
        error: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR'
      }
    }
  }
}

/**
 * Super Admin Login Function
 * @param {string} email - Super admin email
 * @param {string} password - Super admin password
 * @returns {Promise<Object>} Login result
 */
export const loginSuperAdmin = async (email, password) => {
  try {
    console.log('🔐 Sending super admin login request...')
    
    const response = await axios.post('/api/auth/super-admin/login', {
      email: email.trim().toLowerCase(),
      password
    })
    
    const { token, user } = response.data
    
    // Store auth data
    setAuth(token, user)
    
    console.log('✅ Super admin login successful')
    
    return {
      success: true,
      user,
      token
    }
  } catch (error) {
    console.error('Super admin login error:', error)
    
    if (error.response) {
      return {
        success: false,
        error: error.response.data.error || 'Super admin login failed',
        code: error.response.data.code
      }
    } else {
      return {
        success: false,
        error: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR'
      }
    }
  }
}

/**
 * Register new user
 * @param {Object} userData - User registration data
 * @returns {Promise<Object>} Registration result
 */
export const register = async (userData) => {
  try {
    const response = await axios.post('/api/auth/register', userData)
    
    return {
      success: true,
      user: response.data.user,
      message: response.data.message
    }
  } catch (error) {
    console.error('Registration error:', error)
    
    if (error.response) {
      return {
        success: false,
        error: error.response.data.error || 'Registration failed',
        code: error.response.data.code,
        errors: error.response.data.errors
      }
    } else {
      return {
        success: false,
        error: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR'
      }
    }
  }
}

/**
 * Logout user
 * @returns {Promise<void>}
 */
export const logout = async () => {
  try {
    // Check if super admin logout
    if (currentUser?.role === 'super_admin') {
      await logoutSuperAdmin()
      return
    }
    
    // Call logout endpoint to log the action server-side
    await axios.post('/api/auth/logout')
    console.log('✅ Server logout successful')
  } catch (error) {
    console.error('❌ Server logout error:', error)
    // Don't fail logout if server call fails - still clear local data
  } finally {
    // Always clear local auth data regardless of server response
    clearAuth()
    console.log('✅ Local auth data cleared')
  }
}

/**
 * Super Admin Logout Function
 * @returns {Promise<void>}
 */
export const logoutSuperAdmin = async () => {
  try {
    // Call super admin logout endpoint
    await axios.post('/api/auth/super-admin/logout')
    console.log('✅ Super admin server logout successful')
  } catch (error) {
    console.error('❌ Super admin server logout error:', error)
    // Don't fail logout if server call fails - still clear local data
  } finally {
    // Always clear local auth data regardless of server response
    clearAuth()
    console.log('✅ Super admin local auth data cleared')
  }
}

/**
 * Quick logout without server call (for emergency logout)
 */
export const quickLogout = () => {
  clearAuth()
  console.log('🔓 Quick logout completed')
}

/**
 * Refresh user profile data
 * @returns {Promise<Object>} Updated user data
 */
export const refreshProfile = async () => {
  try {
    // Check if super admin
    if (currentUser?.role === 'super_admin') {
      return await getSuperAdminProfile()
    }
    
    const response = await axios.get('/api/auth/profile')
    const userData = response.data.user
    
    // Update stored user data
    localStorage.setItem('user', JSON.stringify(userData))
    currentUser = userData
    
    return {
      success: true,
      user: userData
    }
  } catch (error) {
    console.error('Profile refresh error:', error)
    
    if (error.response?.status === 401) {
      // Token expired or invalid
      clearAuth()
      return {
        success: false,
        error: 'Session expired. Please log in again.',
        code: 'SESSION_EXPIRED'
      }
    }
    
    return {
      success: false,
      error: 'Failed to refresh profile data',
      code: 'PROFILE_ERROR'
    }
  }
}

/**
 * Get Super Admin Profile
 * @returns {Promise<Object>} Profile result
 */
export const getSuperAdminProfile = async () => {
  try {
    const response = await axios.get('/api/auth/super-admin/profile')
    const userData = response.data.user
    
    // Update stored user data
    localStorage.setItem('user', JSON.stringify(userData))
    currentUser = userData
    
    return {
      success: true,
      user: userData
    }
  } catch (error) {
    console.error('Super admin profile fetch error:', error)
    
    if (error.response?.status === 401) {
      // Token expired or invalid
      clearAuth()
      return {
        success: false,
        error: 'Session expired. Please log in again.',
        code: 'SESSION_EXPIRED'
      }
    }
    
    return {
      success: false,
      error: 'Failed to fetch super admin profile',
      code: 'PROFILE_ERROR'
    }
  }
}

/**
 * Send password reset email
 * @param {string} email - User email
 * @returns {Promise<Object>} Reset result
 */
export const forgotPassword = async (email) => {
  try {
    await axios.post('/api/auth/forgot-password', { email })
    
    return {
      success: true,
      message: 'If this email exists in our system, a password reset link has been sent'
    }
  } catch (error) {
    console.error('Forgot password error:', error)
    
    if (error.response) {
      return {
        success: false,
        error: error.response.data.error || 'Failed to send reset email',
        code: error.response.data.code
      }
    } else {
      return {
        success: false,
        error: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR'
      }
    }
  }
}

/**
 * Reset password with token
 * @param {string} token - Reset token
 * @param {string} password - New password
 * @returns {Promise<Object>} Reset result
 */
export const resetPassword = async (token, password) => {
  try {
    await axios.post('/api/auth/reset-password', { token, password })
    
    return {
      success: true,
      message: 'Password reset successfully'
    }
  } catch (error) {
    console.error('Reset password error:', error)
    
    if (error.response) {
      return {
        success: false,
        error: error.response.data.error || 'Failed to reset password',
        code: error.response.data.code,
        errors: error.response.data.errors
      }
    } else {
      return {
        success: false,
        error: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR'
      }
    }
  }
}

/**
 * Resend email verification
 * @param {string} email - User email
 * @returns {Promise<Object>} Resend result
 */
export const resendVerification = async (email) => {
  try {
    await axios.post('/api/auth/resend-verification', { email })
    
    return {
      success: true,
      message: 'Verification email sent successfully'
    }
  } catch (error) {
    console.error('Resend verification error:', error)
    
    if (error.response) {
      return {
        success: false,
        error: error.response.data.error || 'Failed to send verification email',
        code: error.response.data.code
      }
    } else {
      return {
        success: false,
        error: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR'
      }
    }
  }
}

/**
 * Get available schools
 * @returns {Promise<Object>} Schools data
 */
export const getSchools = async () => {
  try {
    const response = await axios.get('/api/auth/schools')
    
    return {
      success: true,
      schools: response.data.schools
    }
  } catch (error) {
    console.error('Get schools error:', error)
    
    return {
      success: false,
      error: 'Failed to load schools',
      schools: []
    }
  }
}

/**
 * Check if user has specific permission
 * @param {string} permission - Permission to check
 * @param {Object} _context - Additional context
 * @returns {boolean} Permission status
 */
export const hasPermission = (permission, _context = {}) => {
  if (!currentUser) return false
  
  // Super admin has all permissions
  if (currentUser.role === 'super_admin') {
    return true
  }
  
  // Administrator has all permissions
  if (currentUser.role === 'administrator') {
    return true
  }
  
  // Check role-based permissions
  switch (permission) {
    case 'manage_school':
      return currentUser.role === 'administrator'
      
    case 'create_events':
    case 'edit_events':
    case 'manage_communications':
      return ['administrator', 'contributor'].includes(currentUser.role)
      
    case 'view_all_classes':
      return currentUser.role === 'administrator'
      
    case 'create_homework':
      return ['administrator', 'contributor'].includes(currentUser.role)
      
    case 'view_homework':
      return true // All authenticated users
      
    case 'access_whatsapp':
      return currentUser.role === 'parent'
      
    case 'view_reports':
      return ['administrator', 'contributor'].includes(currentUser.role)
      
    default:
      return false
  }
}

/**
 * Get user's children (for parents)
 * @returns {Array} Array of children
 */
export const getUserChildren = () => {
  if (currentUser?.role === 'parent' && currentUser.children) {
    return currentUser.children
  }
  return []
}

/**
 * Get user's current class (for students)
 * @returns {Object|null} Current class data
 */
export const getCurrentClass = () => {
  if (currentUser?.role === 'student' && currentUser.currentClass) {
    return currentUser.currentClass
  }
  return null
}

/**
 * Get user's school information
 * @returns {Object|null} School data
 */
export const getUserSchool = () => {
  return currentUser?.school || null
}

/**
 * Get redirect path based on user role
 * @param {string} role - User role
 * @returns {string} Redirect path
 */
export const getRedirectPath = (role) => {
  switch (role) {
    case 'super_admin':
      return '/super-admin/dashboard'
    case 'administrator':
      return '/admin/dashboard'
    case 'contributor':
      return '/teacher/dashboard'
    case 'parent':
      return '/parent/dashboard'
    case 'student':
      return '/student/dashboard'
    default:
      return '/'
  }
}

/**
 * Format user display name
 * @param {Object} user - User object
 * @returns {string} Formatted display name
 */
export const formatDisplayName = (user) => {
  if (!user) return 'Unknown User'
  
  if (user.displayName) {
    return user.displayName
  }
  
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`
  }
  
  return user.email || 'Unknown User'
}

/**
 * Check if user belongs to specific school
 * @param {number} schoolId - School ID to check
 * @returns {boolean} Membership status
 */
export const belongsToSchool = (schoolId) => {
  return currentUser?.school?.id === schoolId
}

/**
 * Validate token expiration
 * @returns {boolean} Token validity
 */
export const isTokenValid = () => {
  const token = localStorage.getItem('token')
  
  if (!token) return false
  
  try {
    // Decode JWT token to check expiration
    const payload = JSON.parse(atob(token.split('.')[1]))
    const currentTime = Date.now() / 1000
    
    return payload.exp > currentTime
  } catch (error) {
    console.error('Error validating token:', error)
    return false
  }
}

/**
 * Handle automatic logout for various scenarios
 * @param {string} reason - Reason for logout
 */
export const handleAutoLogout = (reason = 'unknown') => {
  console.log(`🔓 Auto logout triggered: ${reason}`)
  
  // Clear auth data
  clearAuth()
  
  // Show appropriate message
  let message = 'You have been logged out'
  switch (reason) {
    case 'token_expired':
      message = 'Your session has expired. Please log in again.'
      break
    case 'invalid_token':
      message = 'Invalid session detected. Please log in again.'
      break
    case 'security':
      message = 'For security reasons, you have been logged out.'
      break
    case 'inactivity':
      message = 'You have been logged out due to inactivity.'
      break
  }
  
  // Try to show toast if available
  try {
    const toast = useToast()
    toast.error(message)
  } catch (error) {
    console.log('Toast not available, using alert')
    alert(message)
  }
  
  // Redirect to login if not already there
  if (window.location.pathname !== '/login' && window.location.pathname !== '/super-admin/login') {
    // Determine where to redirect based on user type
    if (currentUser?.role === 'super_admin') {
      window.location.href = '/super-admin/login'
    } else {
      window.location.href = '/login'
    }
  }
}

/**
 * Setup axios interceptors for automatic token handling
 */
export const setupAxiosInterceptors = () => {
  // Request interceptor
  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )
  
  // Response interceptor
  axios.interceptors.response.use(
    (response) => {
      return response
    },
    (error) => {
      if (error.response?.status === 401) {
        // Token expired or invalid
        handleAutoLogout('token_expired')
      }
      
      return Promise.reject(error)
    }
  )
}

// At the bottom of your auth.js file, REMOVE this line:
// initAuth()

// And REPLACE the initAuth function with this safer version:
/**
 * Initialize authentication system (call this manually from main.js)
 */
export const initAuth = () => {
  // Check existing auth WITHOUT triggering side effects
  const token = localStorage.getItem('token')
  const userData = localStorage.getItem('user')
  
  if (token && userData) {
    try {
      currentUser = JSON.parse(userData)
      authState = true
      
      // Only set up axios header if we have valid data
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      
      console.log('Auth initialized with existing session:', currentUser.email, currentUser.role)
    } catch (error) {
      console.error('Error parsing stored user data on init:', error)
      // Clear invalid data quietly
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      currentUser = null
      authState = false
    }
  } else {
    console.log('Auth initialized - no existing session')
    currentUser = null
    authState = false
  }
  
  // Setup axios interceptors (but don't auto-logout here)
  setupAxiosInterceptors()
}

// REMOVE the auto-init call at the bottom of the file
// Don't call initAuth() automatically

/**
 * Check if user is currently authenticated (main function)
 * @returns {boolean} Authentication status
 */
export const isUserAuthenticated = () => {
  return authState && !!currentUser && isTokenValid()
}

/**
 * Check if user is authenticated (alias for compatibility)
 * @returns {boolean} Authentication status
 */
export const isAuthenticated = () => {
  return isUserAuthenticated()
}

/**
 * Check if current user is super admin
 * @returns {boolean} Super admin status
 */
export const isSuperAdmin = () => {
  return currentUser?.role === 'super_admin' && isUserAuthenticated()
}

/**
 * Get Super Admin Statistics
 * @returns {Promise<Object>} Statistics result
 */
export const getSuperAdminStatistics = async () => {
  try {
    const response = await axios.get('/api/auth/super-admin/statistics')
    
    return {
      success: true,
      statistics: response.data.statistics
    }
  } catch (error) {
    console.error('Super admin statistics error:', error)
    
    return {
      success: false,
      error: 'Failed to fetch system statistics',
      code: 'STATS_ERROR'
    }
  }
}

/**
 * Get user display info for UI
 * @returns {Object} User display information
 */
export const getUserDisplayInfo = () => {
  if (!currentUser) return null
  
  return {
    displayName: formatDisplayName(currentUser),
    email: currentUser.email,
    role: currentUser.role,
    roleDisplayName: getRoleDisplayName(currentUser.role),
    school: currentUser.school,
    avatar: currentUser.photo_url || null,
    initials: getInitials(currentUser.displayName || currentUser.email),
    isSuperAdmin: currentUser.role === 'super_admin'
  }
}

/**
 * Get role display name
 * @param {string} role - User role
 * @returns {string} Display name for role
 */
export const getRoleDisplayName = (role) => {
  switch (role) {
    case 'super_admin':
      return 'Super Administrator'
    case 'administrator':
      return 'Administrator'
    case 'contributor':
      return 'Teacher/Staff'
    case 'parent':
      return 'Parent'
    case 'student':
      return 'Student'
    default:
      return 'User'
  }
}

/**
 * Get user initials for avatar
 * @param {string} name - User name
 * @returns {string} User initials
 */
export const getInitials = (name) => {
  if (!name) return 'U'
  
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('')
}

/**
 * Sign out function (alias for logout - for compatibility with http-interceptor)
 * @returns {Promise<void>}
 */
export const signOut = async () => {
  return await logout()
}

// Export auth state for reactive access
export { currentUser }

